import { useCallback } from 'react'
import { useDropzone } from 'react-dropzone'
import {
  UploadCloud,
  FileText,
  Trash2,
  Plus,
  Eye,
  EyeOff,
  RotateCcw,
  ListChecks,
  FilePlus2,
  X,
  Image as ImageIcon,
  Boxes,
} from 'lucide-react'
import { useProjectStore } from '../store/projectStore.js'
import { buildDocumentFromFile } from '../lib/pdfActions.js'
import { buildDocumentFromImage } from '../lib/imageToPdf.js'
import { formatBytes, truncate } from '../lib/format.js'

export default function LeftSidebar({ open: drawerOpen = false, onClose = () => {} }) {
  const documents = useProjectStore((s) => s.documents)
  const selectedDocumentId = useProjectStore((s) => s.selectedDocumentId)
  const splitGroups = useProjectStore((s) => s.splitGroups)
  const addDocument = useProjectStore((s) => s.addDocument)
  const removeDocument = useProjectStore((s) => s.removeDocument)
  const selectDocument = useProjectStore((s) => s.selectDocument)
  const clearAll = useProjectStore((s) => s.clearAll)
  const setAllPagesIncluded = useProjectStore((s) => s.setAllPagesIncluded)
  const invertSelection = useProjectStore((s) => s.invertSelection)
  const setProcessing = useProjectStore((s) => s.setProcessing)
  const showToast = useProjectStore((s) => s.showToast)
  const addSplitGroup = useProjectStore((s) => s.addSplitGroup)
  const renameSplitGroup = useProjectStore((s) => s.renameSplitGroup)
  const removeSplitGroup = useProjectStore((s) => s.removeSplitGroup)

  const handleFiles = useCallback(
    async (files) => {
      if (!files || files.length === 0) return
      setProcessing(true, `Reading ${files.length} file${files.length === 1 ? '' : 's'}…`)
      try {
        for (const file of files) {
          const isImage = /^image\/(png|jpe?g)$/i.test(file.type) ||
            /\.(png|jpe?g)$/i.test(file.name)
          const isPdf = file.type === 'application/pdf' || /\.pdf$/i.test(file.name)
          try {
            if (isPdf) {
              const doc = await buildDocumentFromFile(file)
              addDocument(doc)
            } else if (isImage) {
              const doc = await buildDocumentFromImage(file)
              addDocument(doc)
            } else {
              showToast(`Skipped "${file.name}" — only PDF or PNG/JPG supported.`, 'warning')
            }
          } catch (err) {
            showToast(err.message || `Could not load "${file.name}".`, 'error')
          }
        }
      } finally {
        setProcessing(false)
      }
    },
    [addDocument, setProcessing, showToast]
  )

  const onDrop = useCallback((acceptedFiles) => handleFiles(acceptedFiles), [handleFiles])

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'image/png': ['.png'],
      'image/jpeg': ['.jpg', '.jpeg'],
    },
    noClick: true,
    noKeyboard: true,
    multiple: true,
  })

  const hasDocs = documents.length > 0

  return (
    <aside
      className={`flex w-[88vw] max-w-[20rem] shrink-0 flex-col border-r border-slate-200 bg-white shadow-2xl transition-transform duration-200 ease-out
        fixed inset-y-0 left-0 top-[57px] z-40
        ${drawerOpen ? 'translate-x-0' : '-translate-x-full'}
        md:static md:top-0 md:z-0 md:w-72 md:translate-x-0 md:shadow-none
        dark:border-slate-800 dark:bg-slate-900`}
    >
      <div className="flex items-center justify-between border-b border-slate-200 px-4 py-2 md:hidden dark:border-slate-800">
        <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-100">Documents</h2>
        <button
          type="button"
          onClick={onClose}
          className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
          aria-label="Close documents panel"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="border-b border-slate-200 p-4 dark:border-slate-800">
        <div
          {...getRootProps()}
          className={`relative flex flex-col items-center justify-center gap-1 rounded-xl border-2 border-dashed px-4 py-5 text-center transition-colors ${
            isDragActive
              ? 'border-brand-500 bg-brand-50/60 dark:bg-brand-900/30'
              : 'border-slate-300 bg-slate-50 hover:border-brand-400 hover:bg-brand-50/30 dark:border-slate-700 dark:bg-slate-800/40 dark:hover:bg-slate-800'
          }`}
        >
          <input {...getInputProps()} />
          <UploadCloud
            className={`h-7 w-7 ${
              isDragActive ? 'text-brand-600' : 'text-slate-400'
            }`}
          />
          <p className="text-sm font-medium text-slate-700 dark:text-slate-200">
            {isDragActive ? 'Drop PDFs or images' : 'Drag & drop PDFs or images'}
          </p>
          <p className="text-xs text-slate-500 dark:text-slate-400">.pdf · .png · .jpg</p>
          <button
            type="button"
            onClick={open}
            className="btn-secondary mt-1 h-8 px-3 text-xs dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
          >
            <Plus className="h-3.5 w-3.5" />
            Browse files
          </button>
        </div>
      </div>

      <div className="flex items-center justify-between px-4 pt-4">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-500">Documents</h2>
        <span className="pill dark:bg-slate-800 dark:text-slate-300">
          <FileText className="h-3 w-3" />
          {documents.length}
        </span>
      </div>

      <div className="flex-1 overflow-y-auto px-3 pb-3 pt-2">
        {!hasDocs && (
          <div className="rounded-lg border border-dashed border-slate-200 p-4 text-center text-xs text-slate-500 dark:border-slate-700 dark:text-slate-400">
            Upload a PDF or image to begin.
          </div>
        )}
        <ul className="space-y-1.5">
          {documents.map((doc) => {
            const includedCount = doc.pages.filter((p) => p.included).length
            const isActive = doc.id === selectedDocumentId
            const Icon = doc.sourceKind === 'image' ? ImageIcon : FileText
            return (
              <li key={doc.id}>
                <button
                  type="button"
                  onClick={() => selectDocument(doc.id)}
                  className={`group flex w-full items-start gap-2 rounded-lg border px-2.5 py-2 text-left transition-colors ${
                    isActive
                      ? 'border-brand-200 bg-brand-50/70 dark:border-brand-900 dark:bg-brand-900/30'
                      : 'border-transparent hover:border-slate-200 hover:bg-slate-50 dark:hover:border-slate-700 dark:hover:bg-slate-800'
                  }`}
                >
                  <div
                    className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md ${
                      isActive
                        ? 'bg-brand-100 text-brand-700 dark:bg-brand-900/60 dark:text-brand-200'
                        : 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400'
                    }`}
                  >
                    <Icon className="h-3.5 w-3.5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p
                      className="truncate text-sm font-medium text-slate-800 dark:text-slate-100"
                      title={doc.originalName}
                    >
                      {truncate(doc.originalName, 28)}
                    </p>
                    <p className="mt-0.5 text-[11px] text-slate-500 dark:text-slate-400">
                      {includedCount}/{doc.pages.length} pages · {formatBytes(doc.size)}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation()
                      removeDocument(doc.id)
                    }}
                    className="rounded-md p-1 text-slate-400 opacity-0 transition group-hover:opacity-100 hover:bg-rose-50 hover:text-rose-600"
                    aria-label={`Remove ${doc.originalName}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </button>
              </li>
            )
          })}
        </ul>

        <div className="mt-5 flex items-center justify-between px-1">
          <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-500">Split groups</h2>
          <button
            type="button"
            onClick={() => addSplitGroup()}
            className="rounded-md p-1 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
            title="Add group"
            aria-label="Add split group"
          >
            <Plus className="h-3.5 w-3.5" />
          </button>
        </div>
        {splitGroups.length === 0 ? (
          <p className="mt-1 px-1 text-[11px] text-slate-400">
            Create groups to split export into multiple PDFs.
          </p>
        ) : (
          <ul className="mt-2 space-y-1.5">
            {splitGroups.map((g) => (
              <li
                key={g.id}
                className="flex items-center gap-2 rounded-md border border-slate-200 px-2 py-1.5 dark:border-slate-700"
              >
                <Boxes className="h-3.5 w-3.5 text-slate-400" />
                <input
                  value={g.name}
                  onChange={(e) => renameSplitGroup(g.id, e.target.value)}
                  className="flex-1 border-0 bg-transparent text-xs text-slate-800 outline-none placeholder:text-slate-400 dark:text-slate-100"
                  placeholder="Group name"
                />
                <span className="rounded bg-slate-100 px-1 text-[10px] text-slate-500 dark:bg-slate-800 dark:text-slate-400">
                  {g.pageIds.length}
                </span>
                <button
                  onClick={() => removeSplitGroup(g.id)}
                  className="rounded-md p-1 text-slate-400 hover:bg-rose-50 hover:text-rose-600"
                  aria-label={`Remove group ${g.name}`}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="border-t border-slate-200 bg-slate-50/60 p-3 dark:border-slate-800 dark:bg-slate-800/40">
        <h3 className="px-1 pb-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
          Global actions
        </h3>
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={open}
            className="btn-secondary h-9 px-2 text-xs dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
            title="Add more files"
          >
            <FilePlus2 className="h-3.5 w-3.5" />
            Add files
          </button>
          <button
            type="button"
            onClick={() => setAllPagesIncluded(true)}
            disabled={!hasDocs}
            className="btn-secondary h-9 px-2 text-xs dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
            title="Include all"
          >
            <Eye className="h-3.5 w-3.5" />
            Show all
          </button>
          <button
            type="button"
            onClick={() => setAllPagesIncluded(false)}
            disabled={!hasDocs}
            className="btn-secondary h-9 px-2 text-xs dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
            title="Exclude all"
          >
            <EyeOff className="h-3.5 w-3.5" />
            Hide all
          </button>
          <button
            type="button"
            onClick={() => invertSelection()}
            disabled={!hasDocs}
            className="btn-secondary h-9 px-2 text-xs dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
            title="Invert selection"
          >
            <ListChecks className="h-3.5 w-3.5" />
            Invert
          </button>
        </div>
        <button
          type="button"
          onClick={() => {
            if (window.confirm('Remove all documents and reset the workspace?')) clearAll()
          }}
          disabled={!hasDocs}
          className="btn-danger mt-2 h-9 w-full px-2 text-xs"
        >
          <RotateCcw className="h-3.5 w-3.5" />
          Clear all
        </button>
      </div>
    </aside>
  )
}
